import { useCallback, useEffect, useState } from "react";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { HttpApiService } from "@/shared/services/httpApi.service";
import type { Permission } from "@/shared/types/permission.types";

import { OrganizationsAdminService } from "../services/organizationsAdmin.service";
import type { OrganizationOverview } from "../types/organizationsAdmin.types";

/** Mirrors the endpoint's `@JwtAuth`: it aggregates every module, so it asks for each one's read. */
export const ORGANIZATION_OVERVIEW_PERMISSIONS: readonly Permission[] = [
  "organizations:read",
  "identities:read",
  "users:read",
  "payments:read",
  "cards:read",
  "clients:read",
  "api_key:read",
];

export type OrganizationOverviewState =
  | { status: "idle" }
  | { status: "forbidden" }
  | { status: "loading" }
  | { status: "ready"; overview: OrganizationOverview }
  | { status: "error"; message: string };

export interface UseOrganizationOverview {
  state: OrganizationOverviewState;
  reload: () => void;
}

interface Outcome {
  request: string;
  overview: OrganizationOverview | null;
  error: string | null;
}

// Read on every opening, never cached: staff open the panel to see the organization as it is now.
export function useOrganizationOverview(
  organizationId: string | null,
  open: boolean,
): UseOrganizationOverview {
  const { can } = useAuth();
  const allowed = ORGANIZATION_OVERVIEW_PERMISSIONS.every((permission) => can(permission));
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [opening, setOpening] = useState(0);
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setOpening((value) => value + 1);
  }
  // Not cleared on close, so the panel keeps its content while it animates out.
  const request = organizationId && allowed ? `${organizationId}:${opening}:${attempt}` : null;

  useEffect(() => {
    if (!request || !organizationId || !open) return;
    let cancelled = false;
    OrganizationsAdminService.getOverview(organizationId).then(
      (overview) => {
        if (!cancelled) setOutcome({ request, overview, error: null });
      },
      (error: unknown) => {
        if (!cancelled) setOutcome({ request, overview: null, error: HttpApiService.getErrorMessage(error) });
      },
    );
    return () => {
      cancelled = true;
    };
  }, [request, organizationId, open]);

  const reload = useCallback(() => setAttempt((value) => value + 1), []);

  let state: OrganizationOverviewState;
  if (!allowed) state = { status: "forbidden" };
  else if (!request) state = { status: "idle" };
  else if (outcome?.request !== request) state = { status: "loading" };
  else if (outcome.overview) state = { status: "ready", overview: outcome.overview };
  else state = { status: "error", message: outcome.error ?? "" };
  return { state, reload };
}
